import axios from "axios";

// Base API URL - ajusta esto según tu entorno
const API_BASE_URL = "https://api.vendeplay.com/streaming-head-seller/v1"; // http://localhost:3001/streaming-head-seller/v1

export interface IresponseData<T> {
  code: string;
  message: string;
  status: number;
  meta: {
    traceId: string;
  };
  data: T;
}

export interface IresponseDataPaginated<T> extends IresponseData<T> {
  pagination: {
    totalPages: number;
    totalItems: number;
    pageIndex: number;
    pageSize: number;
  };
}

// Import / Export (compartido entre entidades: clientes, cuentas, proveedores)
export interface ImportRowError {
  rowNumber: number;
  email?: string;
  service?: number;
  reason: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: ImportRowError[];
}

// Constante para la clave del token en localStorage
export const TOKEN_KEY = "auth_token";

// Crear instancia de axios con configuración base
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para agregar el token en cada petición
apiClient.interceptors.request.use(
  (config) => {
    // Obtener el token de localStorage
    const token = localStorage.getItem(TOKEN_KEY);

    // Si existe el token, agregarlo al header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas de error
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log("🚀 - error:", error);
    // Si recibimos un 401, significa que el token expiró o no es válido
    if (error.response?.status === 401) {
      // Limpiar token del localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("user_data");
      // Redirigir al login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Respuesta de los endpoints de exportación: el archivo viaja en base64 dentro del JSON
export interface ExportFilePayload {
  fileBase64: string;
  fileName: string;
  mimeType: string;
}

/**
 * Descarga un archivo (Excel) desde un endpoint de exportación que responde
 * con el archivo codificado en base64 dentro de la envoltura IresponseData.
 */
export async function fetchExportBlob(
  path: string,
  params?: Record<string, unknown>,
  fallbackFileName = "export.xlsx",
): Promise<{ blob: Blob; fileName: string }> {
  const response = await apiClient.get<IresponseData<ExportFilePayload>>(path, {
    params,
  });
  const { fileBase64, fileName, mimeType } = response.data.data;

  const byteChars = atob(fileBase64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: mimeType || "application/octet-stream",
  });

  return { blob, fileName: fileName || fallbackFileName };
}

export default apiClient;
